import {
  Box,
  FieldSelect,
  FormField,
  FormGrid,
  HelperField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Tabs,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { HTTPRequestMethod } from 'lib-shared-legacy-types';
import noop from 'lodash/noop';
import { useCallback, useState } from 'react';
import { Field, FieldArray, Form, InjectedFormProps } from 'redux-form';
import { v4 as uuid } from 'uuid';

import { ajaxSend } from 'api/api';
import {
  DispatchProps,
  StateProps,
} from 'components/powerFlowShared/containers/modals/integration';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FieldAnnouncementPicker } from 'components/shared/announcementPicker';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import AuthGeneratorComponent from 'components/shared/authGeneratorComponent/AuthGeneratorComponent';
import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import {
  FlowManagerEditModalComponentT,
  FlowType,
  RequestSimulator,
} from 'fable/flowEditor';
import { TokenInput } from 'fable/tokenInput';

import type { ErrorTypeT } from '../../containers/modals/validators/integration';
import {
  FlowCategoriesT,
  getAvailableFlowVariables,
} from '../../helpers/flowVariableHelpers';
import { FlowNodeIntegration } from '../nodeTypes';
import type {
  FlowNodeIntegrationPayload,
  Variable,
} from '../nodeTypes/integration';
import { FlowNodeTypes } from '../types';

import { contentTypes, HTMLRequestTypes, tabs } from './integration/constants';
import { HeaderRow } from './integration/headerRow';
import {
  getStatusFromResponse,
  getVariables,
  parseJSONFromResponse,
  Status,
} from './integration/helpers';
import { VariableRow } from './integration/variableRow';
import styles from './integration.module.scss';

export const ANNOUNCEMENT_FIELD = 'announcement';
const BODY_FIELD = 'body';
const CONTENT_TYPE_FIELD = 'contentType';
export const HEADERS_FIELD = 'headers';
const HTTP_REQUEST_TYPE_FIELD = 'httpRequestType';
export const URL_FIELD = 'url';
export const VARIABLES_FIELD = 'variables';

export interface IntegrationModalFormValues
  extends Omit<
    FlowNodeIntegrationPayload,
    'announcement' | 'headers' | 'variables' | 'nextNodeId'
  > {
  announcement?: AnnouncementItem;
  headers: FormHeaders[];
  variables: FormVariables[];
}

export type FormVariables = Variable & {
  locked: boolean;
};

export type FormHeaders = {
  key: string;
  value: string;
};

type Test = {
  loading: boolean;
  data: string | Record<string, string> | null;
  status: Status[];
};

export type Props = FlowManagerEditModalComponentT<FlowNodeIntegration> &
  InjectedFormProps<IntegrationModalFormValues, Props, ErrorTypeT> &
  StateProps &
  DispatchProps;

export function Integration({
  flowType,
  formValues,
  handleSubmit,
  node,
  nodeLabel,
  payload,
  replaceNode,
  scopeVars,
  triggerClose,
  dirty: isChanged,
}: Props): JSX.Element {
  const isPhoneFlow = flowType === FlowType.FLOW_TYPE_CALL;

  const [test, setTest] = useState<Test>({
    loading: false,
    data: null,
    status: [
      {
        name: 'Status',
        text: 'No response.',
        color: 'DEFAULT',
      },
    ],
  });
  const [selectedTab, setSelectedTab] = useState('headers');
  const [hasValidAnnouncement, setHasValidAnnouncement] = useState(true);

  const handleTabSelection = useCallback(
    (tabId: string): void => {
      if (tabId) {
        setSelectedTab(tabId);
      }
    },
    [setSelectedTab],
  );

  const getHeadersForPayload = useCallback(
    (headers: FormHeaders[]): Record<string, string> => {
      return headers.reduce((payloadHeaders, { key, value }) => {
        payloadHeaders[key] = value;
        return payloadHeaders;
      }, {});
    },
    [],
  );

  const handleTestRequest = useCallback(
    (values) => {
      if (formValues) {
        const { url, body, contentType, headers, httpRequestType } = formValues;
        setTest({ ...test, data: null, loading: true });

        const scopeVarRegex = /{{(([^:]*:)?([^}]+))}}/g;
        const replaceFunc = (_fullMatch: string, key: string): string =>
          values?.[key]
            ? JSON.stringify(values?.[key]).replace(/^"(.*)"$/, '$1')
            : '';
        const replacedBody = body.replace(scopeVarRegex, replaceFunc);
        const startTime = Date.now();

        ajaxSend(
          {
            method: HTTPRequestMethod.Post,
            path: '/v1/flows/integration_node/url',
            data: {
              url: url ?? '',
              placeholder_to_value: values,
            },
          },
          true,
        )
          .then(
            (response: any): Promise<any> =>
              new Promise((resolve: (res: Promise<any>) => void) => {
                if (response.data?.data?.is_success) {
                  resolve(
                    ajaxSend<any>(
                      {
                        method: HTTPRequestMethod.Post,
                        path: '/v1/flows/integration_node/validate_http_call',
                        data: {
                          id: response.data.data.id,
                          http_request_type: httpRequestType,
                          body: replacedBody,
                          content_type: contentType,
                          headers: getHeadersForPayload(headers),
                        },
                      },
                      true,
                    ),
                  );
                } else {
                  throw {
                    response: {
                      data: {
                        data: {
                          body: 'Internal Dixa error',
                        },
                      },
                    },
                  };
                }
              }),
          )
          .then((response): void => {
            setTest({
              data: parseJSONFromResponse(response) || null,
              loading: false,
              status: getStatusFromResponse(response, startTime),
            });
          })
          .catch(({ response }): void => {
            setTest({
              data: parseJSONFromResponse(response) || null,
              loading: false,
              status: getStatusFromResponse(response, startTime),
            });
          });
      }
    },
    [formValues, getHeadersForPayload, setTest, test],
  );

  const isValidAnnouncement = useCallback(
    (announcement): boolean => {
      if (isPhoneFlow) {
        const file = announcement?.file || null;
        const isValid = !!file;

        if (isValid !== hasValidAnnouncement) {
          setHasValidAnnouncement(isValid);
        }
        return hasValidAnnouncement;
      }

      return true;
    },
    [isPhoneFlow, hasValidAnnouncement, setHasValidAnnouncement],
  );

  const handleSaveClick = useCallback(
    ({
      announcement,
      body,
      contentType,
      headers,
      httpRequestType,
      url,
      variables,
    }: IntegrationModalFormValues) => {
      if (isValidAnnouncement(announcement)) {
        const headersForPayload = getHeadersForPayload(headers);
        const variablesForPayload: Variable[] = variables.map(
          (variable: Variable) => ({
            ...variable,
            type: 'scope',
          }),
        );

        const nodeOptions: FlowNodeIntegration = {
          ...node,
          nodeType: FlowNodeTypes.INTEGRATION,
          text: 'Fetch data',
          icon: 'i-channels',
          isRemovable: true,
          isEditable: true,
          isAction: false,
          badges: [],
          overlay: [],
          payload: {
            body,
            contentType,
            headers: headersForPayload,
            httpRequestType,
            nextNodeId: payload.nextNodeId || uuid(),
            url,
            variables: variablesForPayload,
            ...(isPhoneFlow &&
              announcement && {
                announcement: {
                  file: {
                    ...announcement,
                    filename: announcement.label,
                  },
                },
              }),
          },
        };

        replaceNode(nodeOptions);
        triggerClose();
      }
    },
    [
      getHeadersForPayload,
      isPhoneFlow,
      isValidAnnouncement,
      node,
      payload,
      replaceNode,
      triggerClose,
    ],
  );

  const availableFlowVariables: FlowCategoriesT = getAvailableFlowVariables({
    flowType,
    scopeVars,
  });

  return (
    <Box width="780px">
      <ModalHead
        className={styles.head}
        onClose={triggerClose}
        title={nodeLabel}
      />
      <Form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalBody>
          <Text.Paragraph subdued>
            Integrate third-party systems or your own API to route conversations
            with the filter node. All conversations can be routed based on
            relevant customer information in Dixa (e.g. previous orders,
            tracking links, etc.).
          </Text.Paragraph>
          <FormGrid className={styles.form}>
            <FormField span={1}>
              <Field
                component={FieldSelect}
                items={HTMLRequestTypes}
                label="Method"
                name={HTTP_REQUEST_TYPE_FIELD}
              />
            </FormField>
            <FormField span={5}>
              <Field
                className={styles.tokenInput}
                component={TokenInput}
                hasNewStyle
                label="URL"
                name={URL_FIELD}
                placeholder="Your api url"
                variables={availableFlowVariables}
              />
            </FormField>

            <FormField>
              <Tabs
                className={styles.tabs}
                onChange={handleTabSelection}
                selectedId={selectedTab}
                tabs={tabs}
              />
            </FormField>

            <FormField
              className={classnames({ hide: selectedTab !== 'headers' })}
            >
              <FieldArray
                actions={AuthGeneratorComponent}
                buttonProps={{
                  className: styles.addButton,
                  label: 'Add header',
                }}
                className={styles.fieldRepeater}
                component={FieldRepeater}
                divider={<RepeaterDivider className={styles.divider} />}
                style={{ maxHeight: 238 }}
                name={HEADERS_FIELD}
                rowComponent={HeaderRow}
                title="Headers"
              />
            </FormField>

            <FormField className={classnames({ hide: selectedTab !== 'body' })}>
              <Field
                component={FieldSelect}
                items={contentTypes}
                label="Content-type"
                name={CONTENT_TYPE_FIELD}
              />

              <Field
                component={TokenInput}
                hasNewStyle
                mode="textarea"
                name={BODY_FIELD}
                noAlert
                noLabel
                placeholder="Content"
                variables={availableFlowVariables}
              />
            </FormField>

            <FormField className={classnames({ hide: selectedTab !== 'test' })}>
              <RequestSimulator
                data={test.data}
                helpText="To check that everything was setup correctly and see test results, send a test request over HTTP/HTTPS."
                isAligned
                loading={test.loading}
                onChange={noop}
                onSend={handleTestRequest}
                status={test.status}
                title="Test HTTP"
                variables={getVariables(availableFlowVariables, formValues)}
              />
            </FormField>

            <FormField>
              <h2 className={styles.subTitle}>Input condition</h2>
              <p className={styles.fieldDescription}>
                Make sure the variable names match the JSON-path in the JSON
                object in the response.
              </p>
              <FieldArray
                buttonProps={{
                  className: styles.addButton,
                  label: 'Add variable',
                }}
                className={styles.fieldRepeater}
                component={FieldRepeater}
                divider={<RepeaterDivider className={styles.divider} />}
                style={{ maxHeight: 238 }}
                name={VARIABLES_FIELD}
                rowComponent={VariableRow}
                rowProps={{ variablesValue: formValues?.variables }}
                title="Variables"
              />
            </FormField>

            {flowType === FlowType.FLOW_TYPE_CALL && (
              <FormField className={styles.announcement}>
                <HelperField
                  className={styles.announcementHeader}
                  fieldId={ANNOUNCEMENT_FIELD}
                  label="Announcement"
                >
                  Fetching data can take a few seconds. Please choose an
                  announcement to play while the data is being fetched.
                </HelperField>
                <Field
                  adapter={AdapterTypes.Announcement}
                  component={FieldAnnouncementPicker}
                  name={ANNOUNCEMENT_FIELD}
                  onSelect={noop}
                />
              </FormField>
            )}
          </FormGrid>
        </ModalBody>
        <ModalFoot
          cancelOnClick={triggerClose}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          confirmOnClick={handleSubmit(handleSaveClick)}
        />
      </Form>
    </Box>
  );
}
